import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { axiosKK } from '../../../services/networkRequest';
import errorMessage from '../../../services/errorMessage';
import { OrderIssueType, StageProgress } from '../../../models/APIModels';
import { WarehouseLocation } from '../../../models/APIModels';
import {
  BoxHistoryResponse,
  BoxHistoryRow,
  StorageSummary,
  WarehouseBoxesResponse,
  WarehouseOrderRow,
  WarehouseStorageResponse,
  BOX_HISTORY_PAGE_SIZE,
  dpdLabelPath,
  groupBoxesByOrder,
  hasActiveLocation,
  pageCount,
  storageSummary,
} from './warehouseRows';
import {
  FilterBar,
  DataTable,
  DataTableColumn,
  ExportCsvButton,
  EmptyState,
  AdminNotice,
  SegmentedControl,
  SectionHead,
} from '../../../components/admin/Shared';
import { DateDropdown } from '../../../components/DateDropdown';
import Button from '../../../components/Button';

type SubTab = 'inbound' | 'outbound' | 'storage' | 'box-history';

// The tab opens on today and this month, as it did before the Phase 12 redesign.
// Opening it empty sent `date=` / `month=`, and storage-api parses whatever it is
// given -- an empty string throws, so every view answered 500 until a date was
// picked. The params below also drop an emptied field, so clearing the picker falls
// back to the API's own default (today / this month) instead of failing.
function monthISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// Today where the admin is. toISOString() is UTC, so in British Summer Time the
// tab opened on yesterday between midnight and 1am.
function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function progressLabel(p?: StageProgress | null): string {
  if (!p) return '—';
  return `${p.scanned ?? 0}/${p.total ?? 0}`;
}

function issuesLabel(items?: { type: OrderIssueType }[]): string {
  if (!items || items.length === 0) return '—';
  return items.map((i) => i.type).join(', ');
}

function returnCourierCell(r: WarehouseOrderRow): React.ReactNode {
  if (r.needsDpdLabel) {
    return (
      <span className="admin-status-pill admin-status-pill--amber" title="DPD is the return courier and this order has no DPD label yet">
        <span className="admin-status-pill__dot" aria-hidden="true" />
        DPD · label needed
      </span>
    );
  }
  if (r.hasDpdLabel) return `${r.returnCourier || 'DPD'} · label created`;
  return r.returnCourier || '—';
}

// Column emphasis: row identity (Customer) is primary, supporting context
// is secondary, system meta (boxes count, scan progress, IDs) is meta.
const INBOUND_COLUMNS: DataTableColumn<WarehouseOrderRow>[] = [
  { key: 'customer', label: 'Customer', emphasis: 'primary', render: (r) => r.customer },
  { key: 'courier', label: 'Courier', emphasis: 'secondary', render: (r) => r.courier || '—' },
  { key: 'date', label: 'Collection', emphasis: 'secondary', render: (r) => r.date || '—' },
  { key: 'eta', label: 'ETA', emphasis: 'secondary', render: (r) => r.eta || '—' },
  { key: 'status', label: 'Status', emphasis: 'secondary', render: (r) => r.status },
  { key: 'unloadProgress', label: 'Unload', emphasis: 'meta', render: (r) => progressLabel(r.unloadProgress) },
  { key: 'boxes', label: 'Boxes', emphasis: 'meta', align: 'numeric', render: (r) => r.boxes },
  { key: 'issues', label: 'Issues', emphasis: 'meta', render: (r) => issuesLabel(r.issues) },
  { key: 'orderId', label: 'Order', emphasis: 'meta', align: 'numeric', render: (r) => `#${r.orderId}` },
];

const OUTBOUND_COLUMNS: DataTableColumn<WarehouseOrderRow>[] = [
  { key: 'customer', label: 'Customer', emphasis: 'primary', render: (r) => r.customer },
  { key: 'returnCourier', label: 'Return courier', emphasis: 'secondary', render: returnCourierCell },
  { key: 'courier', label: 'Driver', emphasis: 'secondary', render: (r) => r.courier || '—' },
  { key: 'date', label: 'Return', emphasis: 'secondary', render: (r) => r.date || '—' },
  { key: 'status', label: 'Status', emphasis: 'secondary', render: (r) => r.status },
  { key: 'pickProgress', label: 'Pick', emphasis: 'meta', render: (r) => progressLabel(r.pickProgress) },
  { key: 'holdoutProgress', label: 'Holdout', emphasis: 'meta', render: (r) => progressLabel(r.holdoutProgress) },
  { key: 'onsProgress', label: 'ONS', emphasis: 'meta', render: (r) => progressLabel(r.onsProgress) },
  { key: 'boxes', label: 'Boxes', emphasis: 'meta', align: 'numeric', render: (r) => r.boxes },
  { key: 'issues', label: 'Issues', emphasis: 'meta', render: (r) => issuesLabel(r.issues) },
  { key: 'orderId', label: 'Order', emphasis: 'meta', align: 'numeric', render: (r) => `#${r.orderId}` },
];

const STORAGE_COLUMNS: DataTableColumn<{ date: string; count: number }>[] = [
  { key: 'date', label: 'Date', emphasis: 'primary', render: (r) => r.date },
  { key: 'count', label: 'Boxes in storage', emphasis: 'secondary', align: 'numeric', render: (r) => r.count },
];

const BOX_HISTORY_COLUMNS: DataTableColumn<BoxHistoryRow>[] = [
  { key: 'name', label: 'Customer', emphasis: 'primary', render: (r) => r.name || '—' },
  { key: 'lastBoxStatus', label: 'Status', emphasis: 'secondary', render: (r) => r.lastBoxStatus || '—' },
  { key: 'lastScanDate', label: 'Last scan', emphasis: 'secondary', render: (r) => r.lastScanDate || '—' },
  { key: 'warehouse', label: 'Location', emphasis: 'secondary', render: (r) => r.warehouse || '—' },
  { key: 'collectionDate', label: 'Collected', emphasis: 'meta', render: (r) => r.collectionDate || '—' },
  { key: 'deliveryDate', label: 'Return', emphasis: 'meta', render: (r) => r.deliveryDate || '—' },
  { key: 'bid', label: 'Box ID', emphasis: 'meta', render: (r) => r.bid || '—' },
  { key: 'oid', label: 'Order', emphasis: 'meta', align: 'numeric', render: (r) => (r.oid != null ? `#${r.oid}` : '—') },
];

const VIEW_OPTIONS: { id: SubTab; label: string }[] = [
  { id: 'inbound', label: 'Inbound' },
  { id: 'outbound', label: 'Outbound' },
  { id: 'storage', label: 'Storage' },
  { id: 'box-history', label: 'Box history' },
];

interface WarehouseTabProps {
  partnerId: number | null;
  /** "All orders" or "Unassigned orders" is picked: scopes the Orders tab has and this one cannot. */
  wholeScope?: boolean;
}

export default function WarehouseTab({ partnerId, wholeScope = false }: WarehouseTabProps) {
  const history = useHistory();
  const [tab, setTab] = useState<SubTab>('inbound');

  const [date, setDate] = useState<string>(todayISO);
  const [month, setMonth] = useState<string>(monthISO);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [issueFilter, setIssueFilter] = useState<OrderIssueType | ''>('');
  // null until known; stays null if the lookup fails, so the views still load.
  const [hasLocation, setHasLocation] = useState<boolean | null>(null);

  const [inbound, setInbound] = useState<WarehouseOrderRow[]>([]);
  const [outbound, setOutbound] = useState<WarehouseOrderRow[]>([]);
  const [storage, setStorage] = useState<StorageSummary>(() => storageSummary(null));
  const [history_, setHistory] = useState<BoxHistoryRow[]>([]);

  const [loading, setLoading] = useState(false);
  const [netError, setNetError] = useState<string>();

  // Every view reads the partner's warehouse locations, and a partner with none
  // has nothing routed to it — so each view answered "no orders on this day",
  // which reads as a quiet day rather than a partner nobody can send boxes to.
  useEffect(() => {
    setHasLocation(null);
    if (partnerId == null) return;
    let cancelled = false;
    axiosKK
      .get<{ warehouses: WarehouseLocation[] }>(`/admin/partners/${partnerId}/warehouses`)
      .then(({ data }) => {
        if (!cancelled) setHasLocation(hasActiveLocation(data.warehouses));
      })
      .catch(() => {
        // Unknown: show the views as before rather than a warning that may be wrong.
      });
    return () => {
      cancelled = true;
    };
  }, [partnerId]);

  // A search is sent once typing pauses, not once per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // A new partner, month or search starts box history again from its first page.
  useEffect(() => {
    setHistoryPage(1);
  }, [partnerId, month, search]);

  useEffect(() => {
    if (partnerId == null) {
      setInbound([]);
      setOutbound([]);
      setStorage(storageSummary(null));
      setHistory([]);
      setHistoryTotal(0);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setNetError(undefined);

    if (tab === 'inbound') {
      const params: Record<string, unknown> = { date: date || undefined, partnerId };
      if (issueFilter) params.issue = issueFilter;
      axiosKK
        .get<WarehouseBoxesResponse>('/logistics/dashboard/warehouse/inbound', { params })
        .then(({ data }) => {
          if (cancelled) return;
          setInbound(groupBoxesByOrder(data.orders || [], 'collectionDate'));
        })
        .catch((err) => { if (!cancelled) errorMessage(setNetError)(err); })
        .finally(() => { if (!cancelled) setLoading(false); });
    } else if (tab === 'outbound') {
      const params: Record<string, unknown> = { date: date || undefined, partnerId };
      if (issueFilter) params.issue = issueFilter;
      axiosKK
        .get<WarehouseBoxesResponse>('/logistics/dashboard/warehouse/outbound', { params })
        .then(({ data }) => {
          if (cancelled) return;
          setOutbound(groupBoxesByOrder(data.orders || [], 'deliveryDate'));
        })
        .catch((err) => { if (!cancelled) errorMessage(setNetError)(err); })
        .finally(() => { if (!cancelled) setLoading(false); });
    } else if (tab === 'storage') {
      axiosKK
        .get<WarehouseStorageResponse>('/logistics/dashboard/warehouse/storage', {
          params: { month: month || undefined, partnerId },
        })
        .then(({ data }) => {
          if (cancelled) return;
          setStorage(storageSummary(data));
        })
        .catch((err) => { if (!cancelled) errorMessage(setNetError)(err); })
        .finally(() => { if (!cancelled) setLoading(false); });
    } else {
      const params: Record<string, unknown> = {
        month: month || undefined,
        partnerId,
        page: historyPage,
        pageSize: BOX_HISTORY_PAGE_SIZE,
      };
      if (search) params.search = search;
      axiosKK
        .get<BoxHistoryResponse>('/logistics/dashboard/warehouse/box-history', { params })
        .then(({ data }) => {
          if (cancelled) return;
          setHistory(data.boxes || []);
          setHistoryTotal(data.totalCount ?? (data.boxes || []).length);
        })
        .catch((err) => { if (!cancelled) errorMessage(setNetError)(err); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }

    return () => {
      cancelled = true;
    };
  }, [tab, date, month, search, issueFilter, partnerId, historyPage]);

  const historyPages = pageCount(historyTotal, BOX_HISTORY_PAGE_SIZE);

  const exportConfig = (() => {
    if (tab === 'inbound') {
      return {
        endpoint: '/logistics/dashboard/warehouse/inbound',
        filename: `warehouse-inbound-${date || todayISO()}.csv`,
        params: {
          date,
          partnerId: partnerId ?? undefined,
          issue: issueFilter || undefined,
        },
      };
    }
    if (tab === 'outbound') {
      return {
        endpoint: '/logistics/dashboard/warehouse/outbound',
        filename: `warehouse-outbound-${date || todayISO()}.csv`,
        params: {
          date,
          partnerId: partnerId ?? undefined,
          issue: issueFilter || undefined,
        },
      };
    }
    if (tab === 'storage') {
      return {
        endpoint: '/logistics/dashboard/warehouse/storage',
        filename: `warehouse-storage-${month || monthISO()}.csv`,
        params: { month, partnerId: partnerId ?? undefined },
      };
    }
    return {
      endpoint: '/logistics/dashboard/warehouse/box-history',
      filename: `warehouse-box-history-${month || monthISO()}.csv`,
      params: {
        month,
        partnerId: partnerId ?? undefined,
        search: search || undefined,
      },
    };
  })();

  const openOrder = (orderId?: number) => {
    if (orderId == null) return;
    history.push(`/admin/order/${orderId}`);
  };

  return (
    <>
      <SectionHead
        title="Warehouse dashboard"
        caption="Inbound is what is due in on the day, Outbound what is due back to customers, Storage how many boxes are held, and Box history every box with its latest scan. Boxes nobody has scanned yet are counted by their collection and return dates."
        infoKey="logistics.warehouse"
      />

      <AdminNotice intent="error">{netError}</AdminNotice>

      <section className="admin-warehouse-forecast">
        <FilterBar
          actions={
            <ExportCsvButton
              endpoint={exportConfig.endpoint}
              filename={exportConfig.filename}
              params={exportConfig.params}
              disabled={partnerId == null || hasLocation === false}
              onError={setNetError}
            />
          }
        >
          <FilterBar.Item label="View">
            <SegmentedControl<SubTab>
              items={VIEW_OPTIONS}
              activeId={tab}
              onChange={setTab}
              ariaLabel="Warehouse views"
            />
          </FilterBar.Item>

          {(tab === 'inbound' || tab === 'outbound') && (
            <>
              <FilterBar.Item label="Date" htmlFor="warehouse-date">
                <DateDropdown
                  id="warehouse-date"
                  value={date || null}
                  onChange={(iso) => setDate(iso ?? '')}
                  label="Date"
                  placeholder="Select date"
                />
              </FilterBar.Item>
              <FilterBar.Item label="Issue" htmlFor="warehouse-issue">
                <select
                  id="warehouse-issue"
                  value={issueFilter}
                  onChange={(e) => setIssueFilter(e.target.value as OrderIssueType | '')}
                >
                  <option value="">All</option>
                  <option value="damaged">Damaged</option>
                  <option value="overweight">Overweight</option>
                  <option value="repack">Repack</option>
                  <option value="other">Other</option>
                </select>
              </FilterBar.Item>
            </>
          )}
          {(tab === 'storage' || tab === 'box-history') && (
            <FilterBar.Item label="Month" htmlFor="warehouse-month">
              <input
                id="warehouse-month"
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
              />
            </FilterBar.Item>
          )}
          {tab === 'box-history' && (
            <FilterBar.Item label="Search" htmlFor="warehouse-search">
              <input
                id="warehouse-search"
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="BID, order id or name"
              />
            </FilterBar.Item>
          )}
        </FilterBar>

        {partnerId == null ? (
          wholeScope ? (
            <EmptyState
              variant="select-prerequisite"
              title="Pick one partner"
              body="The warehouse dashboard shows one partner's warehouse locations at a time. All orders and Unassigned orders are on the Orders tab."
            />
          ) : (
            <EmptyState
              variant="select-prerequisite"
              title="Select a partner"
              body="Pick a partner from the picker above to load the warehouse dashboard."
            />
          )
        ) : hasLocation === false ? (
          <EmptyState
            title="No warehouse location"
            body="This partner has no active warehouse location, so no order can be sent to it and every view here would be empty. Add a location on the partner's Warehouses page, then assign orders to it."
            cta={
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => history.push(`/admin/partners/${partnerId}/warehouses`)}
              >
                Add a warehouse location
              </Button>
            }
          />
        ) : tab === 'inbound' ? (
          <DataTable<WarehouseOrderRow>
            columns={INBOUND_COLUMNS}
            rows={inbound}
            keyOf={(r) => r.orderId}
            loading={loading}
            emptyMessage="No orders are due in on this day."
            onRowClick={(r) => openOrder(r.orderId)}
          />
        ) : tab === 'outbound' ? (
          <DataTable<WarehouseOrderRow>
            columns={OUTBOUND_COLUMNS}
            rows={outbound}
            keyOf={(r) => r.orderId}
            loading={loading}
            emptyMessage="No orders are due out on this day."
            onRowClick={(r) => openOrder(r.orderId)}
            actionsColumn={{
              label: 'DPD',
              mobileLabel: 'DPD',
              render: (r) =>
                r.needsDpdLabel ? (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => history.push(dpdLabelPath(r.orderId))}
                  >
                    Create DPD label
                  </Button>
                ) : null,
            }}
          />
        ) : tab === 'storage' ? (
          <>
            {/* Not while loading: the figure from before (0 on first load) read as today's. */}
            {!loading && (
              <p className="admin-warehouse-storage__summary">
                <strong>{storage.current}</strong> {storage.current === 1 ? 'box' : 'boxes'} in storage today.
                {storage.unscanned > 0 && (
                  <>
                    {' '}
                    {storage.unscanned} of them {storage.unscanned === 1 ? 'has' : 'have'} never been
                    scanned, so {storage.unscanned === 1 ? 'it is' : 'they are'} counted by the
                    collection and return dates.
                  </>
                )}
              </p>
            )}
            <DataTable<{ date: string; count: number }>
              columns={STORAGE_COLUMNS}
              rows={storage.history}
              keyOf={(r) => r.date}
              loading={loading}
              emptyMessage="No storage figures for this month yet."
            />
          </>
        ) : (
          <DataTable<BoxHistoryRow>
            columns={BOX_HISTORY_COLUMNS}
            rows={history_}
            keyOf={(r, i) => r.bid ?? `${r.oid}-${i}`}
            loading={loading}
            emptyMessage={search ? 'No boxes match this search in this month.' : 'No boxes for this month.'}
            onRowClick={(r) => openOrder(r.oid)}
            pagination={
              historyPages > 1 ? (
                <div className="logistics-pagination">
                  <button
                    type="button"
                    className="logistics-pagination__btn"
                    disabled={historyPage <= 1 || loading}
                    onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                    aria-label="Previous page"
                  >
                    &#8249;
                  </button>
                  <span className="logistics-pagination__info">
                    Page {historyPage} of {historyPages} · {historyTotal} boxes
                  </span>
                  <button
                    type="button"
                    className="logistics-pagination__btn"
                    disabled={historyPage >= historyPages || loading}
                    onClick={() => setHistoryPage((p) => Math.min(historyPages, p + 1))}
                    aria-label="Next page"
                  >
                    &#8250;
                  </button>
                </div>
              ) : undefined
            }
          />
        )}
      </section>
    </>
  );
}
